import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { ResourceDataKeys } from '../../../../../../core/constants/resource-data-keys';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { MatChipsModule } from '@angular/material/chips';
import { Observable, startWith, Subject, takeUntil, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { EmissionDesignator } from '../../models/emission-designator';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ExistingLicenseEmissionDesignatorOperationModalData } from './models/existing-license-emission-designator-operation-modal-data';
import { BaseEmissionDesignatorOperationComponent } from '../base-emission-designator-operation/base-emission-designator-operation.component';
import { ExistingLicenseEmissionDesignatorOperation } from '../../models/existing-license-emission-designator-operation';
import { ExistingLicenseEmissionDesignatorOperationFormService } from '../../services/existing-license-emission-designator-operation-form/existing-license-emission-designator-operation-form.service';
import { EmissionDesignatorFormService } from '../../services/emission-designator-form/emission-designator-form.service';
import { EmissionDesignatorConstants } from '../../constants/emission-designator-constants';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';

@Component({
    selector: 'app-existing-license-emission-designator-operation',
    imports: [
        MatButtonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        DraggableModalWrapperComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl:
        './existing-license-emission-designator-operation.component.html',
})
export class ExistingLicenseEmissionDesignatorOperationComponent
    extends BaseEmissionDesignatorOperationComponent<ExistingLicenseEmissionDesignatorOperation>
    implements OnInit, OnDestroy
{
    patterns = RegExPattern;
    emissionDesignatorConstants = EmissionDesignatorConstants;

    destroy$: Subject<void> = new Subject<void>();

    allApprovedFccEmissionDesignators: DropdownOption[] = [];

    commonEmissionDesignatorsFromAllFrequencies: EmissionDesignator[] = [];

    availableToAddEmissionDesignatorOptions: DropdownOption[] = [];

    autoCompleteFilteredAddEmissionDesignatorOptions: DropdownOption[] = [];
    autoCompleteFilteredDeleteEmissionDesignatorOptions: DropdownOption[] = [];

    isManualEmissionOption: boolean = false;

    actionCode = ActionCode;

    form = this.emissionDesignatorOperationFormService.getFormDefinition();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public override data: ExistingLicenseEmissionDesignatorOperationModalData,
        protected override readonly dialogRef: MatDialogRef<ExistingLicenseEmissionDesignatorOperationComponent>,
        private readonly dropdownService: DropdownService,
        private readonly emissionDesignatorOperationFormService: ExistingLicenseEmissionDesignatorOperationFormService,
        private readonly emissionDesignatorFormService: EmissionDesignatorFormService,
    ) {
        super(data, dialogRef);
    }

    get addEmissionDesignatorFormControl(): FormControl {
        return this.form.controls.addEmissionDesignator as FormControl;
    }

    get deleteEmissionDesignatorFormControl(): FormControl {
        return this.form.controls.deleteEmissionDesignator as FormControl;
    }

    get addedEmissionDesignators(): EmissionDesignator[] {
        return this.form.controls.emissionDesignators.value.filter(
            (emissionDesignator: EmissionDesignator) =>
                emissionDesignator.actionCode === ActionCode.Add,
        );
    }

    get deletedEmissionDesignators(): EmissionDesignator[] {
        return this.form.controls.emissionDesignators.value.filter(
            (emissionDesignator: EmissionDesignator) =>
                emissionDesignator.actionCode === ActionCode.Delete,
        );
    }

    get existingEmissionDesignators(): EmissionDesignator[] {
        return this.form.controls.emissionDesignators.value.filter(
            (emissionDesignator: EmissionDesignator) =>
                emissionDesignator.actionCode === null,
        );
    }

    ngOnInit(): void {
        this.getApprovedFccEmissionDesignators().subscribe((data) => {
            this.allApprovedFccEmissionDesignators = data;
            this.availableToAddEmissionDesignatorOptions = data;

            this.commonEmissionDesignatorsFromAllFrequencies =
                this.emissionDesignatorFormService.getCommonEmissionDesignatorsFromAllFrequencies(
                    this.data.controls,
                );

            if (this.singleFrequencyAction === true) {
                this.initializeFormForSingleFrequency();
            } else {
                this.initializeFormForMultipleFrequencies();
            }

            this.addEmissionDesignatorFormControl.valueChanges
                .pipe(
                    startWith(this.addEmissionDesignatorFormControl.value),
                    takeUntil(this.destroy$),
                    tap((value) => {
                        this.filterAddEmissionDesignatorAutoCompleteOptions(
                            value,
                        );

                        this.isManualEmissionOption =
                            value.length > 0 &&
                            this.isManualEmissionDesignatorOption(value);
                    }),
                )
                .subscribe();

            this.deleteEmissionDesignatorFormControl.valueChanges
                .pipe(
                    startWith(this.deleteEmissionDesignatorFormControl.value),
                    takeUntil(this.destroy$),
                    tap((value) => {
                        this.filterDeleteEmissionDesignatorAutoCompleteOptions(
                            value,
                        );
                    }),
                )
                .subscribe();
        });
    }

    onAddEmissionDesignator(event: MatAutocompleteSelectedEvent): void {
        const code = event.option.value as string;
        const emissionDesignator: EmissionDesignator = {
            id: null,
            code,
            actionCode: ActionCode.Add,
            manualEmissionCode: this.isManualEmissionDesignatorOption(code),
        };

        this.emissionDesignatorFormService.addEmissionDesignator(
            emissionDesignator,
            this.emissionDesignatorsFormArray,
        );

        this.refreshAddEmissionDesignatorDropdownOptions();

        this.addEmissionDesignatorFormControl.setValue('');
    }

    onRemoveAddedEmissionDesignator(code: string): void {
        const index = this.emissionDesignatorsFormArray.controls.findIndex(
            (control) => control.value.code === code,
        );

        const emissionDesignator = this.emissionDesignatorsFormArray.at(index);

        if (emissionDesignator.controls.actionCode.value === ActionCode.Add) {
            this.emissionDesignatorsFormArray.removeAt(index);
        } else {
            emissionDesignator.controls.actionCode.setValue(ActionCode.Delete);
        }

        this.form.markAsTouched();

        this.refreshAddEmissionDesignatorDropdownOptions();
    }

    onDeleteEmissionDesignator(event: MatAutocompleteSelectedEvent): void {
        const index = this.emissionDesignatorsFormArray.controls.findIndex(
            (control) => control.value.code === event.option.value,
        );

        if (index === -1) {
            const code = event.option.value as string;
            const emissionDesignator: EmissionDesignator = {
                id: null,
                code,
                actionCode: ActionCode.Delete,
                manualEmissionCode: this.isManualEmissionDesignatorOption(code),
            };

            this.emissionDesignatorFormService.addEmissionDesignator(
                emissionDesignator,
                this.emissionDesignatorsFormArray,
            );

            this.deleteEmissionDesignatorFormControl.setValue('');

            this.form.markAsTouched();

            this.refreshDeleteEmissionDesignatorDropdownOptions();
        } else {
            this.onDeleteEmissionDesignatorWithCode(event.option.value);
        }
    }

    onDeleteEmissionDesignatorWithCode(code: string): void {
        const index = this.emissionDesignatorsFormArray.controls.findIndex(
            (control) => control.value.code === code,
        );

        this.emissionDesignatorsFormArray
            .at(index)
            .controls.actionCode.setValue(ActionCode.Delete);

        this.deleteEmissionDesignatorFormControl.setValue('');

        this.form.markAsTouched();

        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    onRemoveDeletedEmissionDesignator(code: string): void {
        const control = this.emissionDesignatorsFormArray;
        const index = control.controls.findIndex(
            (control) => control.value.code === code,
        );

        const emissionDesignator = control.at(index);

        emissionDesignator.controls.actionCode.setValue(null);

        this.form.markAsTouched();

        this.refreshDeleteEmissionDesignatorDropdownOptions();
        this.refreshAddEmissionDesignatorDropdownOptions();
    }

    onSave(): void {
        if (this.form.touched === true) {
            this.data.saveEmissionDesignatorOperationFunction(
                this.form,
                this.data.controls,
            );
        }

        this.dialogRef.close();
    }

    shouldShowManualEmissionDesignatorOption(): boolean {
        return this.emissionDesignatorFormService.shouldShowManualEmissionDesignatorOption(
            this.isManualEmissionOption,
            this.addEmissionDesignatorFormControl,
        );
    }

    private get emissionDesignatorsFormArray(): FormArray<
        FormGroup<FormGroupType<EmissionDesignator>>
    > {
        return this.form.controls.emissionDesignators as FormArray<
            FormGroup<FormGroupType<EmissionDesignator>>
        >;
    }

    protected isManualEmissionDesignatorOption(code: string): boolean {
        return !this.allApprovedFccEmissionDesignators.some(
            (option) => option.value === code.toUpperCase(),
        );
    }

    private getApprovedFccEmissionDesignators(): Observable<DropdownOption[]> {
        return this.dropdownService.get(ResourceDataKeys.emissionDesignator, {
            allowEmpty: false,
            excludeDescriptions: true,
        });
    }

    private initializeFormForSingleFrequency(): void {
        this.commonEmissionDesignatorsFromAllFrequencies.forEach(
            (emissionDesignator) => {
                this.emissionDesignatorFormService.addEmissionDesignator(
                    emissionDesignator,
                    this.emissionDesignatorsFormArray,
                );
            },
        );

        this.refreshAddEmissionDesignatorDropdownOptions();
        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    private initializeFormForMultipleFrequencies(): void {
        let list = this.allApprovedFccEmissionDesignators;

        this.commonEmissionDesignatorsFromAllFrequencies.forEach(
            (emissionDesignator) => {
                list =
                    this.emissionDesignatorFormService.getEmissionDesignatorsListWithoutCode(
                        list,
                        emissionDesignator.code!,
                    );

                this.emissionDesignatorFormService.addEmissionDesignator(
                    emissionDesignator,
                    this.emissionDesignatorsFormArray,
                );
            },
        );

        this.availableToAddEmissionDesignatorOptions = list;
        this.refreshDeleteEmissionDesignatorDropdownOptions();
    }

    private filterAddEmissionDesignatorAutoCompleteOptions(
        value: string,
    ): void {
        this.autoCompleteFilteredAddEmissionDesignatorOptions =
            this.emissionDesignatorFormService.getFilteredAddEmissionDesignatorAutoCompleteOptions(
                value,
                this.availableToAddEmissionDesignatorOptions,
            );
    }

    private filterDeleteEmissionDesignatorAutoCompleteOptions(
        value: string,
    ): void {
        this.autoCompleteFilteredDeleteEmissionDesignatorOptions =
            this.emissionDesignatorOperationFormService.getFilteredDeleteEmissionDesignatorAutoCompleteOptions(
                value,
                this.data.controls,
            );
    }

    private refreshAddEmissionDesignatorDropdownOptions(): void {
        const list =
            this.emissionDesignatorFormService.getAddEmissionDesignatorDropdownOptions(
                this.data.controls,
                this.allApprovedFccEmissionDesignators,
                this.emissionDesignatorsFormArray,
                this.singleFrequencyAction,
            );

        this.availableToAddEmissionDesignatorOptions = list;
        this.autoCompleteFilteredAddEmissionDesignatorOptions = list;
    }

    private refreshDeleteEmissionDesignatorDropdownOptions(): void {
        const list =
            this.emissionDesignatorOperationFormService.getDeleteEmissionDesignatorDropdownOptions(
                this.data.controls,
                this.emissionDesignatorsFormArray,
            );

        this.autoCompleteFilteredDeleteEmissionDesignatorOptions = list;
    }
}
